import {groupByUniqueKey} from '@benchling/js-utils';
import {TextInput} from '@mantine/core';
import {css, StyleSheet} from 'aphrodite';
import React, {useEffect, useState} from 'react';

import {WorklistInfo} from 'molbio/components/SaveLocationPicker';
import {FilterKey, keyToEmptyFilter, SortKey} from 'search/SearchConstants';
import {FONT_PROXIMA_14, FONT_WEIGHT_PROXIMA_SEMIBOLD} from 'styles/FontConstants';
import * as Colors from 'ui-library/Colors';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import DeleteIcon from 'ui-library/icons/DeleteIcon';
import Menu from 'ui-library/menus/Menu';
import {OptionData} from 'ui-library/menus/MenuConstants';
import OptionContent from 'ui-library/menus/OptionContent';
import SearchableMenu from 'ui-library/menus/SearchableMenu';
import {isEnterKey, isEscapeKey} from 'util/Keyboard';
import * as net from 'util/Net';
import {ElementKeyboardEvent} from 'util/Types';
import {updateWorklists} from 'worklists/WorklistActions';
import {WorklistType, WorklistFormType} from 'worklists/WorklistConstants';
import {WorklistSearchResponse} from 'worklists/WorklistSearchInput';

export interface WorklistSearchInputWithCreationProps {
  disabled?: boolean;
  onWorklistSelect: (worklistInfo: WorklistInfo | null) => void;
  onSelectCreateNewWorklist: (worklistName: string) => void;
  onClearWorklistName: () => void;
  selectedWorklist: WorklistInfo | null;
  worklistTypes: Array<WorklistType>;
  label?: string;
  onEnter?: (e: ElementKeyboardEvent) => void;
}

function getOptionDatasFromResponse(response: WorklistSearchResponse): Array<OptionData> {
  // Put fetched worklists in the store; this helps avoid re-fetching for chip rendering.
  updateWorklists(groupByUniqueKey(response.worklistSummaries, (worklistSummary) => worklistSummary.id));
  return response.worklistSummaries.map((worklistSummary) => ({
    name: worklistSummary.name,
    value: worklistSummary.id,
  }));
}

const CREATE_NEW_SENTINEL = '__create_new__';
const NONE_SENTINEL = '__none__';

/**
 * Enhanced version of WorklistSearchInput that supports creating new worklists with inline text input
 * when the "Create new worklist" option is selected. This follows the same pattern as EntryTableDropdownWithCreation.
 */
export function WorklistSearchInputWithCreation({
  disabled,
  onWorklistSelect,
  onSelectCreateNewWorklist,
  onClearWorklistName,
  selectedWorklist,
  worklistTypes,
  label = 'Worklist',
  onEnter,
}: WorklistSearchInputWithCreationProps): JSX.Element {
  const [optionDatas, setOptionDatas] = useState<Array<OptionData>>([]);
  const [isLoadingOptions, setIsLoadingOptions] = useState(false);

  const isCreatingWorklist = selectedWorklist?.worklistFormType === WorklistFormType.NEW;
  const worklistName = selectedWorklist?.newWorklistInfo?.name ?? '';

  useEffect(() => {
    const fetchWorklists = async (): Promise<void> => {
      setIsLoadingOptions(true);
      try {
        // Bug: this hard-codes the number of results and with no pagination support.
        const response = await net.postJSONPromise({
          url: '/1/api/worklists:search',
          data: {
            limit: 10,
            filters: [{...keyToEmptyFilter(FilterKey.WORKLIST_TYPE), value: worklistTypes}],
            offset: 0,
            sorts: [{key: SortKey.SORT_MODIFIED_AT_KEY, reverse: true}],
          },
        });
        setOptionDatas(getOptionDatasFromResponse(response));
      } catch (error) {
        setOptionDatas([]);
        throw error;
      } finally {
        setIsLoadingOptions(false);
      }
    };

    if (worklistTypes.length > 0) {
      fetchWorklists();
    }
  }, [worklistTypes]);

  const getDisplayName = (): React.ReactNode => {
    if (
      selectedWorklist?.worklistFormType === WorklistFormType.EXISTING &&
      selectedWorklist.targetWorklistId
    ) {
      const selectedOption = optionDatas.find((option) => option.value === selectedWorklist.targetWorklistId);
      return selectedOption?.name ?? 'Search worklists...';
    }
    return 'Search worklists...';
  };

  const getSelectedValue = (): string | undefined => {
    if (selectedWorklist?.worklistFormType === WorklistFormType.EXISTING) {
      return selectedWorklist.targetWorklistId ?? undefined;
    }
    return NONE_SENTINEL;
  };

  return (
    <div>
      {label && <div className={css(styles.label)}>{label}</div>}
      {!isCreatingWorklist ? (
        <div>
          <SelectDropdown
            disabled={disabled || isLoadingOptions}
            displayName={isLoadingOptions ? 'Loading worklists...' : getDisplayName()}
            closeOnInsideClick={true}
            menu={
              <SearchableMenu
                width={Menu.WIDTHS.FULL}
                height={Menu.HEIGHTS.MEDIUM}
                onSelect={(value) => {
                  if (value === CREATE_NEW_SENTINEL) {
                    onSelectCreateNewWorklist('');
                  } else if (value === NONE_SENTINEL) {
                    onWorklistSelect(null);
                  } else {
                    onWorklistSelect({
                      worklistFormType: WorklistFormType.EXISTING,
                      targetWorklistId: value,
                      newWorklistInfo: null,
                    });
                  }
                }}
                optionDatas={[
                  {
                    name: 'None',
                    value: NONE_SENTINEL,
                  },
                  {
                    name: 'Create new worklist',
                    value: CREATE_NEW_SENTINEL,
                    className: css(styles.createWorklistOption),
                  },
                  ...optionDatas,
                ]}
                selectedValue={getSelectedValue()}
                scrollable={true}
                renderOptionContent={(optionData) => (
                  <OptionContent
                    childrenClassName={
                      optionData.value === CREATE_NEW_SENTINEL ? css(styles.createWorklistOption) : undefined
                    }
                  >
                    {optionData.name}
                  </OptionContent>
                )}
              />
            }
          />
        </div>
      ) : (
        <div className={css(styles.inputContainer)}>
          <TextInput
            className={css(styles.textInput)}
            value={worklistName}
            onChange={(e: React.ChangeEvent<HTMLInputElement>): void => {
              onSelectCreateNewWorklist(e.target.value);
            }}
            onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
              if (isEnterKey(e)) {
                if (worklistName.trim()) {
                  onSelectCreateNewWorklist(worklistName.trim());
                }
                onEnter?.(e);
              } else if (isEscapeKey(e)) {
                onClearWorklistName();
              }
            }}
            placeholder="Enter worklist name"
            autoFocus={true}
          />
          <DeleteIcon icon="cross-thick" onClick={onClearWorklistName} />
        </div>
      )}
    </div>
  );
}

const styles = StyleSheet.create({
  label: {
    ...FONT_PROXIMA_14,
    fontWeight: FONT_WEIGHT_PROXIMA_SEMIBOLD,
    marginBottom: '4px',
    color: Colors.NEAR_BLACK,
  },
  inputContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
  },
  textInput: {
    flex: 1,
  },
  createWorklistOption: {
    color: Colors.BLUE,
  },
  dropdown: {
    flex: 1,
  },
});
